/** The door registry: register a door, correct its details, remove one.
 *
 * `POST/PATCH/DELETE /doors` have always existed and this console only ever read the list, so
 * registering a new door terminal or fixing a mistyped controller address meant curl
 * (docs/FRONTEND-AUDIT.md F-P1-2). A mistyped address is the interesting case: the door reads
 * permanently offline and the watchdog can never verify it, which is exactly the state this
 * whole product exists to notice - and the fix was unavailable in the UI that was showing it.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as adminApi from "../../api/admin.api.js";
import * as doorsApi from "../../api/doors.api.js";
import { DataTable } from "../../components/DataTable.js";
import { Modal } from "../../components/Modal.js";
import { EditIcon, PlusIcon, TrashIcon } from "../../components/Icon.js";
import { useToast } from "../../components/Toast.js";
import { ApiError } from "../../api/client.js";
import { operatorMessage } from "../../lib/api-message.js";
import type { Door, DoorCreateInput } from "../../types/api.js";

const POLICIES = [
  { value: "observe", label: "observe — report and alert, never write" },
  { value: "restore", label: "restore — attempt a bounded restore of an unauthorised override" },
  { value: "off", label: "off — the watchdog ignores this door entirely" },
] as const;

export function RegistryPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<Door | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Door | null>(null);
  const [error, setError] = useState<string | null>(null);

  const doors = useQuery({ queryKey: ["doors"], queryFn: doorsApi.listDoors });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["doors"] });
    void queryClient.invalidateQueries({ queryKey: ["status"] });
  };

  const create = useMutation({
    mutationFn: adminApi.createDoor,
    onMutate: () => setError(null),
    onSuccess: (door) => {
      setCreating(false);
      setError(null);
      invalidate();
      toast.show(`${door.name} was registered.`);
    },
    onError: (err: Error) =>
      setError(operatorMessage(err, "Unable to register that door. Please try again.")),
  });

  const update = useMutation({
    mutationFn: ({ doorId, input }: { doorId: string; input: Partial<DoorCreateInput> }) =>
      adminApi.updateDoor(doorId, input),
    onMutate: () => setError(null),
    onSuccess: (door) => {
      setEditing(null);
      setError(null);
      invalidate();
      toast.show(`${door.name} was updated.`);
    },
    onError: (err: Error) =>
      setError(operatorMessage(err, "Unable to save those changes. Please try again.")),
  });

  const remove = useMutation({
    mutationFn: (door: Door) => adminApi.deleteDoor(door.doorId),
    onMutate: () => setError(null),
    // `door` is the second argument - the variables the mutation was called with. Reading the
    // name from `confirmDelete` here would race the setState below and could name the wrong
    // door, or none at all, in the confirmation the operator actually reads.
    onSuccess: (_result, door) => {
      setConfirmDelete(null);
      setError(null);
      invalidate();
      toast.show(`${door.name} was removed from the registry.`);
    },
    // The API refuses a delete while an override is active, with a specific code and a reason
    // that explains itself. Surfacing THAT rather than "the request failed" is the difference
    // between an operator understanding the refusal and filing a bug about it. Anything the
    // API did NOT write for a person becomes one plain sentence instead.
    // "Please try again" is the wrong advice for a failure the server rejected outright - it
    // sends the operator round a loop that cannot terminate. The sentence says what happened
    // and leaves the retry decision to them; `operatorMessage` appends the correlation id so
    // the actual cause is one search away in the service log.
    onError: (err: Error) => {
      const message = operatorMessage(
        err,
        "This door could not be removed. The server rejected the request and recorded why.",
      );
      setError(message);
      toast.show(message, "critical");
    },
  });

  const list = doors.data ?? [];
  const deleteBlocked = remove.error instanceof ApiError && remove.error.code === "OVERRIDE_ACTIVE";

  /** A dialog is an overlay: an error rendered on the page BEHIND one is an error nobody reads.
   * Every one of these mutations is started from inside a modal, so the message goes in the
   * modal, and the page banner is kept for the case where nothing is covering it. */
  const modalOpen = creating || editing !== null || confirmDelete !== null;

  function dismissModals() {
    setCreating(false);
    setEditing(null);
    setConfirmDelete(null);
    setError(null);
    remove.reset();
  }

  return (
    <>
      <div className="page-head">
        <h1 id="page-heading" tabIndex={-1}>
          Door registry
        </h1>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setError(null);
            setCreating(true);
          }}
        >
          <PlusIcon aria-hidden="true" focusable="false" />
          Register a door
        </button>
      </div>

      <p className="page-intro">
        Which physical controllers this service knows about. Changing an address here changes which
        piece of hardware a command reaches — every change is audited against your name.
      </p>

      {error && !modalOpen ? (
        <div
          className={deleteBlocked ? "banner banner-warning" : "banner banner-critical"}
          role="alert"
        >
          <span className="banner-icon" aria-hidden="true">
            {deleteBlocked ? "⚠" : "✗"}
          </span>
          <div className="banner-body">{error}</div>
        </div>
      ) : null}

      <DataTable
        caption="Registered doors and their controller details"
        isLoading={doors.isLoading}
        isError={doors.isError}
        isEmpty={list.length === 0}
        loadingMessage="Loading the registry…"
        errorMessage="The registry could not be loaded."
        emptyMessage="No doors are registered yet."
        columns={
          <>
            <th scope="col">Door</th>
            <th scope="col">Location</th>
            <th scope="col">Controller</th>
            <th scope="col">Device</th>
            <th scope="col">Pulse</th>
            <th scope="col">Watchdog</th>
            <th scope="col">Enabled</th>
            <th scope="col">Actions</th>
          </>
        }
      >
        {list.map((door) => (
          <tr key={door.doorId}>
            <td>
              <div className="cell-name">{door.name}</div>
              <div className="cell-sub mono">{door.doorId}</div>
            </td>
            <td>{door.location}</td>
            <td className="mono">{door.controllerAddress}</td>
            <td className="mono">{door.deviceId}</td>
            <td className="cell-sub">
              {door.pulseSeconds === null ? "default" : `${door.pulseSeconds}s`}
            </td>
            <td className="mono">{door.watchdogPolicy}</td>
            <td>
              <span className={door.isEnabled ? "status tone-ok" : "status tone-muted"}>
                <span className="status-icon" aria-hidden="true">
                  {door.isEnabled ? "✓" : "·"}
                </span>
                <span>{door.isEnabled ? "enabled" : "disabled"}</span>
              </span>
            </td>
            <td>
              {/* Row-level actions, as they have always been. The labels name the door as well
                  as the verb: "Edit" repeated down eight rows tells a screen-reader user which
                  button they are on but not which door it belongs to. */}
              <div className="row-gap">
                <button
                  type="button"
                  className="btn"
                  aria-label={`Edit ${door.name}`}
                  title={`Edit ${door.name}`}
                  onClick={() => {
                    setError(null);
                    setEditing(door);
                  }}
                >
                  <EditIcon aria-hidden="true" focusable="false" />
                  Edit
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  aria-label={`Remove ${door.name} from the registry`}
                  title={`Remove ${door.name} from the registry`}
                  onClick={() => {
                    setError(null);
                    remove.reset();
                    setConfirmDelete(door);
                  }}
                >
                  <TrashIcon aria-hidden="true" focusable="false" />
                  Remove
                </button>
              </div>
            </td>
          </tr>
        ))}
      </DataTable>

      {creating ? (
        <DoorForm
          busy={create.isPending}
          error={error}
          onCancel={dismissModals}
          onSubmit={(input) => create.mutate(input as DoorCreateInput)}
        />
      ) : null}

      {editing ? (
        <DoorForm
          door={editing}
          busy={update.isPending}
          error={error}
          onCancel={dismissModals}
          onSubmit={(input) => update.mutate({ doorId: editing.doorId, input })}
        />
      ) : null}

      {confirmDelete ? (
        <Modal
          title={`Remove ${confirmDelete.name}?`}
          tone="critical"
          // Not dismissible while the request is in flight: an Escape or a backdrop click
          // mid-delete would close the dialog over a call that is still going to land, and the
          // operator would be left not knowing whether the door went.
          dismissible={!remove.isPending}
          onClose={dismissModals}
          footer={
            <>
              <button
                type="button"
                className="btn"
                disabled={remove.isPending}
                onClick={dismissModals}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={remove.isPending}
                onClick={() => remove.mutate(confirmDelete)}
              >
                <TrashIcon aria-hidden="true" focusable="false" />
                {remove.isPending ? "Removing…" : "Remove door"}
              </button>
            </>
          }
        >
          <p>
            Are you sure you want to remove <strong>{confirmDelete.name}</strong> from the registry?
            The service will stop watching it, and the watchdog will never look at it again.{" "}
            <strong>This cannot be undone.</strong>
          </p>

          {/* The failure, INSIDE the dialog. This used to render as a page banner underneath
              the modal backdrop, so a refused delete showed the operator nothing at all: the
              button simply went back from "Removing…" to its label. */}
          {error ? (
            <div
              className={deleteBlocked ? "banner banner-warning" : "banner banner-critical"}
              role="alert"
            >
              <span className="banner-icon" aria-hidden="true">
                {deleteBlocked ? "⚠" : "✗"}
              </span>
              <div className="banner-body">{error}</div>
            </div>
          ) : null}

          {confirmDelete.overrideActive ? (
            <div className="banner banner-critical">
              <span className="banner-icon" aria-hidden="true">
                ✗
              </span>
              <div className="banner-body">
                This door is in a <strong>persistent override</strong> right now. The API will
                refuse to remove it — deleting the row would lose the only record that a physical
                door is standing with verification suspended. Release the override first.
              </div>
            </div>
          ) : (
            <p className="field-hint">
              The audit history of this door is <strong>not</strong> deleted — it is append-only and
              stays readable in the audit log.
            </p>
          )}
        </Modal>
      ) : null}
    </>
  );
}

function DoorForm({
  door,
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  door?: Door;
  busy: boolean;
  /** A refusal from the API. Rendered in the dialog rather than on the page behind it. */
  error?: string | null;
  onCancel: () => void;
  onSubmit: (input: Partial<DoorCreateInput> & { isEnabled?: boolean }) => void;
}) {
  const editing = door !== undefined;
  const [doorId, setDoorId] = useState(door?.doorId ?? "");
  const [name, setName] = useState(door?.name ?? "");
  const [location, setLocation] = useState(door?.location ?? "");
  const [description, setDescription] = useState(door?.description ?? "");
  const [controllerAddress, setControllerAddress] = useState(door?.controllerAddress ?? "");
  const [deviceId, setDeviceId] = useState(String(door?.deviceId ?? ""));
  const [pulseSeconds, setPulseSeconds] = useState(
    door?.pulseSeconds === null || door?.pulseSeconds === undefined
      ? ""
      : String(door.pulseSeconds),
  );
  const [watchdogPolicy, setWatchdogPolicy] = useState(door?.watchdogPolicy ?? "observe");
  const [isEnabled, setIsEnabled] = useState(door?.isEnabled ?? true);
  const [problem, setProblem] = useState<string | null>(null);

  function submit() {
    // The API validates all of this again. These checks exist so the operator is told at the
    // field rather than by a 422 they have to decode.
    if (!editing && !/^[a-z0-9][a-z0-9-]*$/.test(doorId)) {
      return setProblem("The door id must be a lowercase slug, e.g. 'side-entrance'.");
    }
    if (name.trim().length === 0) return setProblem("Give the door a name.");
    if (location.trim().length === 0) return setProblem("Give the door a location.");

    const device = Number(deviceId);
    if (!Number.isInteger(device) || device < 1 || device > 255) {
      return setProblem("The device id must be a whole number between 1 and 255.");
    }

    let pulse: number | null = null;
    if (pulseSeconds !== "") {
      pulse = Number(pulseSeconds);
      if (!Number.isInteger(pulse) || pulse < 1 || pulse > 30) {
        return setProblem("The pulse must be a whole number of seconds between 1 and 30.");
      }
    }
    setProblem(null);

    const common = {
      name: name.trim(),
      location: location.trim(),
      description: description.trim() === "" ? null : description.trim(),
      controllerAddress: controllerAddress.trim(),
      deviceId: device,
      pulseSeconds: pulse,
      watchdogPolicy,
    };

    onSubmit(editing ? { ...common, isEnabled } : { ...common, doorId: doorId.trim() });
  }

  return (
    <Modal
      title={editing ? `Edit ${door.name}` : "Register a door"}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={submit}>
            {busy ? "Saving…" : editing ? "Save changes" : "Register the door"}
          </button>
        </>
      }
    >
      {/* Two different failures, both shown where the person is looking: `problem` is this
          form's own check, `error` is the API's refusal of the submitted values. */}
      {(problem ?? error) ? (
        <div className="banner banner-critical" role="alert">
          <span className="banner-icon" aria-hidden="true">
            ✗
          </span>
          <div className="banner-body">{problem ?? error}</div>
        </div>
      ) : null}

      {!editing ? (
        <div className="field">
          <label htmlFor="door-id">Door id</label>
          <input
            id="door-id"
            className="mono"
            value={doorId}
            onChange={(e) => setDoorId(e.target.value)}
          />
          <p className="field-hint">
            Lowercase letters, digits and hyphens. Permanent — it is what every audit row for this
            door is keyed on.
          </p>
        </div>
      ) : null}

      <div className="field">
        <label htmlFor="door-name">Name</label>
        <input id="door-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="door-location">Location</label>
        <input id="door-location" value={location} onChange={(e) => setLocation(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="door-description">Description</label>
        <input
          id="door-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="door-address">Controller address</label>
        <input
          id="door-address"
          className="mono"
          value={controllerAddress}
          onChange={(e) => setControllerAddress(e.target.value)}
        />
        <p className="field-hint">
          <code>172.10.10.235</code>, or <code>host:port</code> for a non-standard port (the default
          is 5005). An IPv6 address with a port must be bracketed: <code>[fe80::12]:5005</code>.
        </p>
      </div>

      <div className="field">
        <label htmlFor="door-device">Device id</label>
        <input
          id="door-device"
          className="mono"
          type="number"
          min="1"
          max="255"
          value={deviceId}
          onChange={(e) => setDeviceId(e.target.value)}
        />
        <p className="field-hint">
          1–255. Two doors cannot share an address and a device id — one physical door with two
          registry rows would make the watchdog fight itself.
        </p>
      </div>

      <div className="field">
        <label htmlFor="door-pulse">Pulse (seconds)</label>
        <input
          id="door-pulse"
          type="number"
          min="1"
          max="30"
          value={pulseSeconds}
          placeholder="use the service default"
          onChange={(e) => setPulseSeconds(e.target.value)}
        />
        <p className="field-hint">
          How long a momentary open holds the door before it is restored to Normal. 1–30 seconds;
          leave blank for the service default.
        </p>
      </div>

      <div className="field">
        <label htmlFor="door-policy">Watchdog policy</label>
        <select
          id="door-policy"
          value={watchdogPolicy}
          onChange={(e) => setWatchdogPolicy(e.target.value as Door["watchdogPolicy"])}
        >
          {POLICIES.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
        <p className="field-hint">
          The watchdog never reverts an override a human authorised, whichever policy is set. This
          decides only what it does about an override <em>nobody</em> authorised.
        </p>
      </div>

      {editing ? (
        <div className="field">
          <label htmlFor="door-enabled">
            <input
              id="door-enabled"
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
            />{" "}
            Enabled
          </label>
          <p className="field-hint">
            A disabled door accepts no commands and the watchdog skips it. Use it for a controller
            that has been physically removed — not to silence one that is misbehaving, because that
            is the one you want watched.
          </p>
        </div>
      ) : null}
    </Modal>
  );
}
